import { useEffect, useState } from "react";
import { SearchableSelect } from "@/components/shared/SearchableSelect";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import client, { getErrorMessage } from "@/api/client";
import { toast } from "sonner";

const EQUIPMENT_CHOICES = ["Crane", "Rig", "Other"];

export default function PileFormDialog({ pile, onClose, onSaved, isOfficeRole = false, officeSites = [] }) {
  const isEdit = !!pile;
  const needsSitePick = isOfficeRole && !isEdit;
  const [officeSite, setOfficeSite] = useState("");
  const [config, setConfig] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(!needsSitePick);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    date: pile?.date ? pile.date.slice(0, 10) : new Date().toISOString().slice(0, 10),
    pile_seg1: pile?.pile_seg1 || "",
    pile_seg2: pile?.pile_seg2 || "",
    pile_seg3: pile?.pile_seg3 || "",
    chainage: pile?.chainage || "",
    machine_asset_id: pile?.machine_asset_id || "",
    diameter_mm: pile?.diameter_mm ?? "",
    achieved_depth_m: pile?.achieved_depth_m ?? "",
    cage_lowering_by: pile?.cage_lowering_by || "",
    cage_length: pile?.cage_length ?? "",
    cage_weight_kg: pile?.cage_weight_kg ?? "",
    concreting_by: pile?.concreting_by || "",
    concrete_quantity_m3: pile?.concrete_quantity_m3 ?? "",
    liner_length_m: pile?.liner_length_m ?? "",
    stratum_soil_from: pile?.stratum_soil_from ?? "",
    stratum_soil_to: pile?.stratum_soil_to ?? "",
    stratum_weathered_rock_from: pile?.stratum_weathered_rock_from ?? "",
    stratum_weathered_rock_to: pile?.stratum_weathered_rock_to ?? "",
    stratum_hard_rock_from: pile?.stratum_hard_rock_from ?? "",
    stratum_hard_rock_to: pile?.stratum_hard_rock_to ?? "",
    ogl: pile?.ogl ?? "",
    fl: pile?.fl ?? "",
    ctl: pile?.ctl ?? "",
    col: pile?.col ?? "",
    remarks: pile?.remarks || "",
  });

  useEffect(() => {
    if (needsSitePick && !officeSite) {
      setConfig(null);
      return;
    }
    setLoadingConfig(true);
    const params = isEdit ? { site: pile.site } : needsSitePick ? { site: officeSite } : {};
    client
      .get("/pile-records/config", { params })
      .then((res) => setConfig(res.data))
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => setLoadingConfig(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [officeSite]);

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));
  const num = (v) => (v === "" || v === null || v === undefined ? undefined : parseFloat(v));

  const todayIST = () => {
    const now = new Date();
    const ist = new Date(now.getTime() + (330 + now.getTimezoneOffset()) * 60000);
    return ist.toISOString().slice(0, 10);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (needsSitePick && !officeSite) return toast.error("Site is required");
    if (!form.pile_seg1.trim() && !form.pile_seg2.trim() && !form.pile_seg3.trim()) return toast.error("Pile ID is required");
    if (!form.chainage.trim()) return toast.error("Location/Chainage is required");
    if (pile?.status === "Completed" && form.achieved_depth_m === "") return toast.error("Achieved Depth is required on a Completed pile.");
    setSaving(true);
    const payload = {
      date: new Date(form.date).toISOString(),
      pile_seg1: form.pile_seg1.trim() || null,
      pile_seg2: form.pile_seg2.trim() || null,
      pile_seg3: form.pile_seg3.trim() || null,
      chainage: form.chainage.trim(),
      machine_asset_id: form.machine_asset_id || null,
      diameter_mm: num(form.diameter_mm),
      achieved_depth_m: num(form.achieved_depth_m),
      cage_lowering_by: form.cage_lowering_by || null,
      cage_length: num(form.cage_length),
      cage_weight_kg: num(form.cage_weight_kg),
      concreting_by: form.concreting_by || null,
      concrete_quantity_m3: num(form.concrete_quantity_m3),
      liner_length_m: num(form.liner_length_m),
      stratum_soil_from: num(form.stratum_soil_from),
      stratum_soil_to: num(form.stratum_soil_to),
      stratum_weathered_rock_from: num(form.stratum_weathered_rock_from),
      stratum_weathered_rock_to: num(form.stratum_weathered_rock_to),
      stratum_hard_rock_from: num(form.stratum_hard_rock_from),
      stratum_hard_rock_to: num(form.stratum_hard_rock_to),
      ogl: num(form.ogl),
      fl: num(form.fl),
      ctl: num(form.ctl),
      col: num(form.col),
      remarks: form.remarks.trim() || null,
    };
    if (needsSitePick) payload.site = officeSite;
    try {
      const res = isEdit ? await client.put(`/pile-records/${pile.id}`, payload) : await client.post("/pile-records", payload);
      toast.success(isEdit ? "Pile Record updated" : "Pile Record created");
      onSaved(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const showLiner = config?.support_method === "Permanent Liner";
  const showStratum = config?.rate_structure === "Layered";

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="pile-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit Pile Record — ${pile.pile_id_display}` : "Add Pile Record"}</DialogTitle>
        </DialogHeader>
        {needsSitePick && (
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Site *</label>
            <SearchableSelect testId="pile-form-office-site-select" value={officeSite} onValueChange={setOfficeSite} placeholder="Select site"
  options={officeSites.map((s) => ({ value: s.code, label: s.display_label || s.label, testId: `pile-form-office-site-option-${s.code}` }))} />
          </div>
        )}
        {needsSitePick && !officeSite ? (
          <p className="text-sm text-slate-400 py-8 text-center">Select a site to continue.</p>
        ) : loadingConfig ? (
          <p className="text-sm text-slate-400 py-8 text-center">Loading site config...</p>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            {!needsSitePick && (
              <p className="text-xs text-slate-500">
                Site: <span className="font-medium text-slate-700">{config?.display_label}</span>
              </p>
            )}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Date</label>
                <Input
                  type="date"
                  data-testid="pile-form-date-input"
                  value={form.date}
                  max={needsSitePick ? todayIST() : undefined}
                  onChange={(e) => set("date", e.target.value)}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Chainage / Location *</label>
                <Input list="pile-chainage-suggestions" data-testid="pile-form-chainage-input" value={form.chainage} onChange={(e) => set("chainage", e.target.value)} required />
                <datalist id="pile-chainage-suggestions">
                  {(config?.chainage_suggestions || []).map((c) => (
                    <option key={c} value={c} />
                  ))}
                </datalist>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Machine</label>
                <SearchableSelect testId="pile-form-machine-select" value={form.machine_asset_id || "none"} onValueChange={(v) => set("machine_asset_id", v === "none" ? "" : v)} placeholder="None"
  options={[{ value: "none", label: "None", pinned: true }, ...(config?.machines || []).map((m) => ({ value: m.asset_id, label: m.label, testId: `pile-form-machine-option-${m.asset_id}` }))]} />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Pile ID — Segment 1 *</label>
                <Input data-testid="pile-form-seg1-input" value={form.pile_seg1} onChange={(e) => set("pile_seg1", e.target.value)} />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Pile ID — Segment 2</label>
                <Input data-testid="pile-form-seg2-input" value={form.pile_seg2} onChange={(e) => set("pile_seg2", e.target.value)} />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Pile ID — Segment 3</label>
                <Input data-testid="pile-form-seg3-input" value={form.pile_seg3} onChange={(e) => set("pile_seg3", e.target.value)} />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Diameter (mm)</label>
                <Input type="number" step="any" data-testid="pile-form-diameter-input" value={form.diameter_mm} onChange={(e) => set("diameter_mm", e.target.value)} />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                  Achieved Depth (m) {pile?.status === "Completed" && <span className="text-red-500">*</span>}
                </label>
                <Input type="number" step="any" data-testid="pile-form-achieved-depth-input" value={form.achieved_depth_m} onChange={(e) => set("achieved_depth_m", e.target.value)} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 border-t border-slate-200 pt-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Cage Lowering By</label>
                <Select value={form.cage_lowering_by || "none"} onValueChange={(v) => set("cage_lowering_by", v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="pile-form-cage-lowering-by-select">
                    <SelectValue placeholder="None" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {EQUIPMENT_CHOICES.map((c) => (
                      <SelectItem key={c} value={c} data-testid={`pile-form-cage-lowering-by-option-${c}`}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Cage Length (m)</label>
                  <Input type="number" step="any" data-testid="pile-form-cage-length-input" value={form.cage_length} onChange={(e) => set("cage_length", e.target.value)} />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Cage Weight (kg)</label>
                  <Input type="number" step="any" data-testid="pile-form-cage-weight-input" value={form.cage_weight_kg} onChange={(e) => set("cage_weight_kg", e.target.value)} />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Concreting By</label>
                <Select value={form.concreting_by || "none"} onValueChange={(v) => set("concreting_by", v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="pile-form-concreting-by-select">
                    <SelectValue placeholder="None" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {EQUIPMENT_CHOICES.map((c) => (
                      <SelectItem key={c} value={c} data-testid={`pile-form-concreting-by-option-${c}`}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Concrete Quantity (m³)</label>
                <Input type="number" step="any" data-testid="pile-form-concrete-qty-input" value={form.concrete_quantity_m3} onChange={(e) => set("concrete_quantity_m3", e.target.value)} />
              </div>
              {showLiner && (
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Liner Length (m)</label>
                  <Input type="number" step="any" data-testid="pile-form-liner-length-input" value={form.liner_length_m} onChange={(e) => set("liner_length_m", e.target.value)} />
                </div>
              )}
            </div>

            {showStratum && (
              <div className="border-t border-slate-200 pt-4">
                <p className="text-sm font-medium text-slate-700 mb-2">Stratum (From / To, m)</p>
                <div className="grid grid-cols-3 gap-4">
                  {[
                    ["Soil", "stratum_soil_from", "stratum_soil_to"],
                    ["Weathered Rock", "stratum_weathered_rock_from", "stratum_weathered_rock_to"],
                    ["Hard Rock", "stratum_hard_rock_from", "stratum_hard_rock_to"],
                  ].map(([label, fromKey, toKey]) => (
                    <div key={label} className="space-y-1.5">
                      <p className="text-xs text-slate-500">{label}</p>
                      <div className="flex gap-2">
                        <Input type="number" step="any" placeholder="From" data-testid={`pile-form-${fromKey}-input`} value={form[fromKey]} onChange={(e) => set(fromKey, e.target.value)} />
                        <Input type="number" step="any" placeholder="To" data-testid={`pile-form-${toKey}-input`} value={form[toKey]} onChange={(e) => set(toKey, e.target.value)} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-4 gap-4 border-t border-slate-200 pt-4">
              {["ogl", "fl", "ctl", "col"].map((k) => (
                <div key={k}>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block uppercase">{k}</label>
                  <Input type="number" step="any" data-testid={`pile-form-${k}-input`} value={form[k]} onChange={(e) => set(k, e.target.value)} />
                </div>
              ))}
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Remarks</label>
              <Textarea data-testid="pile-form-remarks-input" value={form.remarks} onChange={(e) => set("remarks", e.target.value)} />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose} data-testid="pile-form-cancel-button">
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid="pile-form-submit-button" className="bg-amber-600 hover:bg-amber-700">
                {saving ? "Saving..." : isEdit ? "Save Changes" : "Create Pile Record"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
